import { classNames } from "@/utils/classNames";

import { ButtonColor, ButtonSize, ButtonStatus } from "./types";

/**
 * MACONIA 2026 button styling — shared by `Button` and `HoveredButton` so the brand
 * rules live in one place. See docs/brand-implementation-guide.md §4.1 and §6.2.
 *
 * The two rules that shape everything below:
 *
 *  1. The primary CTA is a flat `accent` fill, hover `accent-dark`, white **bold**
 *     label at ≥ 16px. Flat, not a gradient, because a flat colour is the only CTA
 *     that reproduces identically in a PDF and an HTML email.
 *  2. White on `accent` is 3.12:1 — legal only for a large bold label. So a small
 *     primary button *cannot* be orange: it falls back to `ink` (18.9:1). That is
 *     the guide's own answer to "we need an extra-small primary button".
 */

/** Sizes whose label is big enough to carry a white-on-accent CTA. */
const CTA_SIZES: ReadonlySet<ButtonSize> = new Set([ButtonSize.M, ButtonSize.ML, ButtonSize.L]);

/** The colours that mean "this is the primary action on the screen". */
const CTA_COLORS: ReadonlySet<ButtonColor> = new Set([ButtonColor.BRAND, ButtonColor.ACTION]);

export const usesAccentFill = (color: ButtonColor, size: ButtonSize): boolean =>
	CTA_COLORS.has(color) && CTA_SIZES.has(size);

export const getButtonColorClasses = (
	color: ButtonColor,
	size: ButtonSize,
	disabledInGray?: boolean
): string => {
	switch (color) {
		case ButtonColor.BRAND:
		case ButtonColor.ACTION:
			// §4.1 the CTA — or `ink` where the label is too small to carry white
			// on orange. §4.2: this is the one thing on the screen that is orange.
			return usesAccentFill(color, size)
				? "bg-accent hover:bg-accent-dark text-white border-transparent !font-bold"
				: "bg-ink hover:bg-neutral-700 text-white border-transparent";

		// A second solid button is never the CTA — orange marks one thing per view.
		case ButtonColor.PRIMARY_BLUE:
			return "bg-ink hover:bg-neutral-700 text-white border-transparent";

		// #3DA318 carries white only at large bold sizes, so the solid green button
		// uses the data scale's `severity-low` (#198754, 4.53:1) and works at any size.
		case ButtonColor.PRIMARY:
		case ButtonColor.SUCCESS:
			return "bg-severity-low text-white border-transparent";

		case ButtonColor.ACTION_SECONDARY:
			return classNames(
				"bg-white border border-border text-text hover:border-accent",
				disabledInGray ? "disabled:!border-border-soft disabled:!bg-transparent" : ""
			);

		case ButtonColor.DANGER:
		case ButtonColor.ERROR:
			return "bg-danger border-danger text-white";

		case ButtonColor.DANGER_SECONDARY:
			return "bg-white border-danger text-danger";

		// §6.2 — white on amber is 2.27:1. Put ink on amber, never white.
		case ButtonColor.WARNING:
			return "bg-warning text-ink border-transparent";

		case ButtonColor.DISABLED:
			return "bg-border-soft text-muted border-transparent";

		case ButtonColor.DECISION:
			return "border border-border-soft bg-severity-low text-white rounded-card";
	}
};

/**
 * §4.5 / §6.1 — a ghost button's label is `accent-strong`, the only orange that may
 * be written. `accent` itself would be 3.12:1 and fails as text.
 */
export const getButtonStatusClasses = (status?: ButtonStatus): string | undefined => {
	switch (status) {
		case ButtonStatus.SUCCESS:
		case ButtonStatus.ACTION:
			return "!border-accent-strong !bg-white !text-accent-strong";
	}
};
